import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { Button, Container, Input } from 'reactstrap';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  MANAGER_REPORT_BASE_PATH,
  getManagerReportMeta,
  matchesManagerReportPath,
  normalizeManagerReportType,
} from './reportRegistry';

const Report = ({ program }) => {
  const [reportTypes, setReportTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  let navigate = useNavigate();
  const location = useLocation();
  const routeReportMeta = useMemo(
    () => getManagerReportMeta(location.pathname),
    [location.pathname]
  );
  const activeReport = useMemo(
    () => reportTypes.find((item) => matchesManagerReportPath(location.pathname, item.link)) || null,
    [location.pathname, reportTypes]
  );

  useEffect(() => {
    if (!program?.id) {
      navigate('/');
      return undefined;
    }

    let isMounted = true;

    const fetchReports = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`/reports/${program.id}`);
        const normalizedReportTypes = (response.data || []).map(normalizeManagerReportType);

        if (isMounted) {
          setReportTypes(normalizedReportTypes);
        }
      } catch (error) {
        console.error("Error fetching reports:", error);
        if (isMounted) {
          setReportTypes([]);
          navigate(MANAGER_REPORT_BASE_PATH, { replace: true });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    fetchReports();

    return () => {
      isMounted = false;
    };
  }, [navigate, program?.id]);

  useEffect(() => {
    if (reportTypes.length === 0) {
      return;
    }

    const hasMatchingRoute = reportTypes.some((item) =>
      matchesManagerReportPath(location.pathname, item.link)
    );
    const isRootReportPath = location.pathname === MANAGER_REPORT_BASE_PATH;
    const firstReportPath = reportTypes[0].path;

    if (isRootReportPath || !hasMatchingRoute) {
      navigate(firstReportPath, { replace: true });
    }
  }, [location.pathname, navigate, reportTypes]);

  const onChange = (e) => {
    navigate(getManagerReportMeta(e.target.value).path);
  };

  const currentReport = activeReport || routeReportMeta;
  const selectedValue = currentReport?.slug || '';
  return (
    <div className="manager-report">
      <Container className="manager-home__shell manager-home__content">
        <section className="manager-report__hero">
          <div className="manager-report__hero-card">
            <div className="manager-report__hero-copy">
              <h1 className="manager-report__title">{currentReport?.title || 'Reports'}</h1>
              <p className="manager-report__description">
                {currentReport?.description || 'Review operational report data, switch report types quickly, and export the current view without leaving the manager workspace.'}
              </p>
            </div>
            <div className="manager-report__hero-controls">
              <div className="manager-report__control">
                <span className="manager-report__control-label">Report</span>
                <div className="manager-report__control-body">
                  {isLoading ? (
                    <span className="manager-report__control-text">Loading...</span>
                  ) : reportTypes.length > 0 ? (
                    <Input
                      type="select"
                      value={selectedValue}
                      name="report-type"
                      onChange={onChange}
                      className="manager-report__select"
                    >
                      {reportTypes.map((item, index) => (
                        <option key={index} value={item.slug}>{item.title}</option>
                      ))}
                    </Input>
                  ) : (
                    <span className="manager-report__control-text">No reports available.</span>
                  )}
                </div>
              </div>
              <Button
                type="button"
                className="manager-report__scheduled-btn mb-0"
                onClick={() => navigate('/manager/scheduled-reports')}
              >
                Scheduled Reports
              </Button>
            </div>
          </div>
        </section>

        <div className="manager-report__content">
          <Outlet context={{ activeReport }} />
        </div>
      </Container>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    program: state.program,
  };
};

export default connect(mapStateToProps)(Report);
